// AGFEO click-to-dial helper.
//
// The AGFEO Dashboard CTI software (installed locally on each calling PC)
// registers a custom URI protocol handler. Navigating the browser to
// `tksuite:<number>` hands the number to the Dashboard, which places the call
// through the configured account — the mechanism AGFEO documents for
// browser-initiated dialing (Klick2Dial).
//
// IMPORTANT: the protocol is ONE-WAY. It returns nothing — no ringing / busy /
// connected state and no duration. `dial()` can only report that the number was
// handed off to the OS/AGFEO Dashboard, NOT that the call actually connected.
// Any call outcome or duration has to be captured separately (manual logging,
// or a future native TAPI bridge). The `useCallTimer` hook below exists so that
// separate logging step can measure duration off a real stopwatch.

import { useState, useRef, useCallback, useEffect } from "react";

const DEFAULT_DIAL_PROTOCOL = "tksuite:";
const MOBILE_DIAL_PROTOCOL = "tel:";

// Default scheme, exported for reference. Prefer `getDialProtocol()` at call
// time so the runtime override is respected.
export const DIAL_PROTOCOL = DEFAULT_DIAL_PROTOCOL;

// Die AGFEO Dashboard-Software ist eine Windows-Desktop-Anwendung, die den
// `tksuite:`-Handler registriert — auf Handy/Tablet gibt es sie nicht, und dort
// wuerde die URI ohnehin ins Leere laufen. `tel:` ist dort der eingebaute Weg,
// die native Telefon-App zu oeffnen.
//
// Kein Feature-Test moeglich (custom URI-Handler lassen sich aus Sicherheitsgruenden
// nicht abfragen, anders als der SAP-Client mit seiner localhost-API in
// useSapStatus.js) — deshalb ueber das Geraet entschieden. User-Agent-Sniffing ist
// nicht wasserdicht (Spoofing, ein Desktop-Chrome im Handy-Modus), aber Industriestandard
// fuer genau diese Frage, und der Fall, der hier zaehlt (Firmen-PC vs. privates Handy),
// faellt darunter zuverlaessig.
function isMobileDevice() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // iPadOS 13+ gibt sich als Mac-Safari aus, hat aber Multitouch — ein echter Mac nicht.
  const isIpadOS = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return /Android|iPhone|iPod|iPad|Mobile/i.test(ua) || isIpadOS;
}

// Resolve the protocol scheme. Allow overriding it at runtime via the
// localStorage key `dialProtocol`, so an alternate handler can be tested on a
// real machine (the `tksuite:` assumption still needs one live verification)
// without shipping a code change.
export function getDialProtocol() {
  try {
    const override = window.localStorage.getItem("dialProtocol");
    if (override && override.trim()) {
      return override.trim();
    }
  } catch (e) {
    // localStorage can be unavailable (private mode / restricted) — ignore.
  }
  return isMobileDevice() ? MOBILE_DIAL_PROTOCOL : DEFAULT_DIAL_PROTOCOL;
}

// Remove trunk prefix in parentheses like "+49 (0) 521 / 447090",
// (+49) or leading (0).
export function cleanTrunkAnomalies(raw) {
  if (!raw) return "";
  return String(raw)
    .trim()
    .replace(/\(0\)/g, "")
    .replace(/\(\+49\)/g, "+49")
    .replace(/\(0049\)/g, "+49");
}

// Keep a leading "+" (international prefix) and digits; drop spaces, dashes,
// slashes, parentheses and any other formatting characters.
export function sanitizeNumber(raw) {
  if (!raw) return "";
  let str = cleanTrunkAnomalies(raw);
  if (str.startsWith("00")) {
    str = "+" + str.slice(2);
  }
  const hasPlus = str.startsWith("+");
  let digits = str.replace(/[^\d]/g, "");
  // Fix German numbers with misplaced trunk zero: +490... -> +49...
  if (hasPlus && digits.startsWith("490") && digits.length >= 10) {
    digits = "49" + digits.slice(3);
  }
  return (hasPlus ? "+" : "") + digits;
}

/**
 * Normalizes any phone number input into the canonical database format:
 * - Removes (0) and unnecessary characters
 * - Converts national numbers (e.g. 0521 447090) or numbers starting with 00 to +<CC><number>
 * - Preserves extension with a hyphen: +49521447090-12
 * - Removes all spaces
 */
export function canonicalPhoneNumber(raw, defaultDialCode = DEFAULT_COUNTRY_CODE) {
  if (!raw) return "";
  const cleaned = cleanTrunkAnomalies(raw);
  if (!cleaned) return "";

  // Check extension
  const extMatch = cleaned.match(/^(.*?)(?:\s*[-/]\s*|(?:\s+(?:ext|dw|durchwahl|app)\.?\s*))(\d{1,5})$/i);
  let mainPart = cleaned;
  let ext = "";
  if (extMatch && extMatch[1].replace(/[^\d]/g, "").length >= 4) {
    mainPart = extMatch[1];
    ext = extMatch[2];
  }

  let sanitized = sanitizeNumber(mainPart);
  if (!sanitized) return "";

  // If number starts with +, it is already international
  if (sanitized.startsWith("+")) {
    return sanitized + (ext ? `-${ext}` : "");
  }

  // If starts with 00, replace with +
  if (sanitized.startsWith("00")) {
    return `+${sanitized.slice(2)}` + (ext ? `-${ext}` : "");
  }

  // If national number starting with 0 (e.g. 0521...), prepend default country code (e.g. +49)
  if (sanitized.startsWith("0")) {
    return `+${defaultDialCode}${sanitized.slice(1)}` + (ext ? `-${ext}` : "");
  }

  // If starts with default dial code already (e.g. 49521...)
  if (sanitized.startsWith(defaultDialCode)) {
    return `+${sanitized}` + (ext ? `-${ext}` : "");
  }

  return sanitized + (ext ? `-${ext}` : "");
}

// ---------------------------------------------------------------------------
// Rueckwaertssuche (AGFEO-Anrufererkennung)
// ---------------------------------------------------------------------------

// Der Laendervorwahl-Default fuer Nummern ohne eigene Angabe.
export const DEFAULT_COUNTRY_CODE = "49";

// Reine Ziffernfolge einer Nummer — die Form, in der der SearchTokens-Index
// Rufnummern fuehrt und in der AGFEO seine Datenquellen abfragt (Handbuch S. 120:
// "0521447090", ohne "+", ohne Trennzeichen).
export function phoneDigits(raw) {
  return String(raw ?? "").replace(/\D/g, "");
}

/**
 * Kennt eine der Karteien (Firma, Ansprechpartner) diese Nummer schon? Vergleich
 * ueber die reinen Ziffern — dieselbe Nummer wird in vier Schreibweisen gepflegt.
 * Eine leere Nummer gilt als bekannt: es gibt nichts zu hinterlegen.
 */
export function numberIsKnown(number, records = []) {
  // Ueber phoneTokens, nicht ueber die rohen Ziffern: "+49 170 1234567" und
  // "0170/1234567" sind dieselbe Nummer, ihre Ziffernfolgen sind es nicht.
  // Eine zu kurze Nummer gilt als bekannt — sie soll kein Zuordnen anbieten.
  if (new Set(phoneTokens(number)).size === 0) return true;
  return Boolean(findByNumber(number, records));
}

/**
 * Der erste Datensatz, dessen `phone` oder `mobil` dieselbe Nummer ist — fuer
 * eingehende Anrufe: die Rufnummer ist alles, was die Anlage liefert, der
 * Ansprechpartner muss daraus fallen. Gleiche Token-Logik wie numberIsKnown.
 */
export function findByNumber(number, records = []) {
  const tokens = new Set(phoneTokens(number));
  if (tokens.size === 0) return null;
  return (
    records.find((r) =>
      phoneTokens(r?.phone, r?.mobil).some((token) => tokens.has(token))
    ) || null
  );
}

/**
 * Alle Schreibweisen derselben Rufnummer, wie sie im SearchTokens-Index abgelegt
 * werden: roh, national mit fuehrender 0, international ohne "+" und mit "00".
 * Wir wissen nicht, in welcher Form eine Nummer gepflegt wurde und in welcher
 * AGFEO fragt — ein Token kostet praktisch nichts, eine fehlende Variante den
 * Treffer.
 *
 * MUSS mit phoneTokens() in
 * amplify/backend/function/lineupdashboardlineupdashboardLayer/opt/searchTokens.js
 * uebereinstimmen — dort werden die Token geschrieben, hier die Abfrage gebildet.
 * Dasselbe Doppel wie bei fold()/tokenize(); laufen die beiden auseinander,
 * findet die Rueckwaertssuche nichts mehr.
 */
export function phoneTokens(...values) {
  const out = new Set();
  for (const value of values) {
    if (!value) continue;
    const cleaned = cleanTrunkAnomalies(value);

    // Wenn Durchwahl vorhanden (z. B. "+49 521 447090-12"), auch den
    // Zentralenstamm als Suchtoken aufnehmen, damit die Firma auch bei
    // ankommenden Rufen auf die Kopfnummer gefunden wird.
    const extMatch = cleaned.match(/^(.*?)(?:\s*[-/]\s*|(?:\s+(?:ext|dw|durchwahl|app)\.?\s*))(\d{1,5})$/i);
    const variants = [cleaned];
    if (extMatch && extMatch[1].replace(/[^\d]/g, "").length >= 4) {
      variants.push(extMatch[1]);
    }

    for (const v of variants) {
      let digits = phoneDigits(v);
      if (digits.startsWith("490") && digits.length >= 10) {
        digits = "49" + digits.slice(3);
      }
      // Kuerzer als 5 Ziffern ist eine interne Nebenstelle oder Muell — als Token
      // waere das ein Praefix, das auf hunderte Nummern passt.
      if (digits.length < 5) continue;

      out.add(digits);
      if (digits.startsWith("00")) {
        const intl = digits.slice(2);
        out.add(intl);
        if (intl.startsWith(DEFAULT_COUNTRY_CODE)) {
          out.add("0" + intl.slice(DEFAULT_COUNTRY_CODE.length));
        }
      } else if (digits.startsWith("0")) {
        const intl = DEFAULT_COUNTRY_CODE + digits.slice(1);
        out.add(intl);
        out.add("00" + intl);
      } else if (digits.startsWith(DEFAULT_COUNTRY_CODE)) {
        out.add("0" + digits.slice(DEFAULT_COUNTRY_CODE.length));
        out.add("00" + digits);
      } else {
        // Nicht-deutsche Auslandsnummer (z. B. 43... fuer AT, 41... fuer CH):
        // Auch mit '00' indexieren, da deutsche TK-Anlagen eingehende Auslandsanrufe
        // typischerweise als 00<Land><Nummer> signalisieren.
        out.add("00" + digits);
      }
    }
  }
  return [...out];
}

/**
 * Formats a phone number for clean readability (DIN 5008 / E.123).
 * Separates country code, area code, and subscriber number with spaces,
 * and preserves extensions with a hyphen.
 */
export function formatPhoneNumber(raw) {
  if (!raw) return "";
  const trimmed = String(raw).trim();
  if (!trimmed) return "";

  // Check for extension suffix (e.g. "-12", "- 12", " ext 12", " DW 12")
  let mainPart = trimmed;
  let extension = "";

  // Remove trunk prefix in parentheses like "+49 (0) 521 / 447090"
  mainPart = mainPart.replace(/\(0\)/g, "").replace(/\(\+49\)/g, "+49");

  const extMatch = mainPart.match(/^(.*?)(?:\s*[-/]\s*|(?:\s+(?:ext|dw|durchwahl|app)\.?\s*))(\d{1,5})$/i);
  if (extMatch && extMatch[1].replace(/[^\d+]/g, "").length >= 4) {
    mainPart = extMatch[1];
    extension = extMatch[2];
  }

  // Canonical clean digits and leading plus
  let sanitized = sanitizeNumber(mainPart);
  if (!sanitized) return trimmed;

  // Convert "0049..." to "+49..."
  if (sanitized.startsWith("00")) {
    sanitized = "+" + sanitized.slice(2);
  }

  const hasPlus = sanitized.startsWith("+");
  const digits = sanitized.replace(/[^\d]/g, "");

  if (digits.length < 4) {
    return trimmed;
  }

  let formatted = "";

  // 1. Germany (+49 or national 0...)
  if (sanitized.startsWith("+49") || (!hasPlus && sanitized.startsWith("0"))) {
    const isIntl = sanitized.startsWith("+49");
    const rest = isIntl ? digits.slice(2) : digits.slice(1);
    const prefix = "+49";

    if (rest.length === 0) return trimmed;

    // 2-digit German area codes (Berlin 30, Hamburg 40, Frankfurt 69, München 89)
    const twoDigit = ["30", "40", "69", "89"];
    // 3-digit major German area codes
    const threeDigit = [
      "201", "202", "203", "208", "209",
      "211", "212", "214", "215", "221", "228", "231", "234", "241",
      "341", "351", "361", "371", "381", "391",
      "421", "431", "451",
      "511", "521", "531", "541", "551", "561",
      "611", "621", "631", "651", "681",
      "711", "721", "731", "751", "761",
      "800", "821", "831", "841", "851", "861", "871",
      "911", "921", "931", "941", "951",
    ];

    let areaCode = "";
    let subscriber = "";

    if (twoDigit.includes(rest.slice(0, 2))) {
      areaCode = rest.slice(0, 2);
      subscriber = rest.slice(2);
    } else if (threeDigit.includes(rest.slice(0, 3))) {
      areaCode = rest.slice(0, 3);
      subscriber = rest.slice(3);
    } else if (/^1[567]/.test(rest)) {
      // Mobile numbers (15x, 16x, 17x)
      if (/^15\d{2}/.test(rest)) {
        areaCode = rest.slice(0, 4);
        subscriber = rest.slice(4);
      } else {
        areaCode = rest.slice(0, 3);
        subscriber = rest.slice(3);
      }
    } else if (rest.length >= 8) {
      areaCode = rest.slice(0, 4);
      subscriber = rest.slice(4);
    } else if (rest.length >= 6) {
      areaCode = rest.slice(0, 3);
      subscriber = rest.slice(3);
    } else {
      areaCode = rest;
      subscriber = "";
    }

    if (isIntl || hasPlus) {
      formatted = `${prefix} ${areaCode}${subscriber ? " " + subscriber : ""}`;
    } else {
      formatted = `0${areaCode}${subscriber ? " " + subscriber : ""}`;
    }
  }
  // 2. North America (+1)
  else if (sanitized.startsWith("+1") && digits.length === 11) {
    const area = digits.slice(1, 4);
    const exchange = digits.slice(4, 7);
    const line = digits.slice(7, 11);
    formatted = `+1 ${area} ${exchange} ${line}`;
  }
  // 3. Austria (+43)
  else if (sanitized.startsWith("+43")) {
    const rest = digits.slice(2);
    if (rest.startsWith("1")) {
      formatted = `+43 1 ${rest.slice(1)}`;
    } else if (rest.length >= 6) {
      formatted = `+43 ${rest.slice(0, 3)} ${rest.slice(3)}`;
    } else {
      formatted = `+43 ${rest}`;
    }
  }
  // 4. Switzerland (+41)
  else if (sanitized.startsWith("+41")) {
    const rest = digits.slice(2);
    if (rest.length >= 9) {
      formatted = `+41 ${rest.slice(0, 2)} ${rest.slice(2, 5)} ${rest.slice(5, 7)} ${rest.slice(7)}`;
    } else {
      formatted = `+41 ${rest.slice(0, 2)} ${rest.slice(2)}`;
    }
  }
  // 5. France (+33)
  else if (sanitized.startsWith("+33")) {
    const rest = digits.slice(2);
    const pairs = rest.match(/.{1,2}/g) || [];
    formatted = `+33 ${pairs.join(" ")}`;
  }
  // 6. Generic international (+CC ...)
  else if (hasPlus) {
    const cc = digits.length > 10 ? digits.slice(0, 2) : digits.slice(0, 2);
    const rest = digits.slice(cc.length);
    const grouped = rest.replace(/(\d{3,4})(?=\d)/g, "$1 ");
    formatted = `+${cc} ${grouped}`;
  }
  // 7. Generic national
  else {
    formatted = digits.replace(/(\d{3,4})(?=\d)/g, "$1 ");
  }

  if (extension) {
    formatted += `-${extension}`;
  }

  return formatted.trim();
}

// True when the input has enough digits to be a real phone number.
export function isDialable(raw) {
  return sanitizeNumber(raw).replace("+", "").length >= 3;
}

// Build the full dial URI, e.g. "tksuite:+49521447090".
export function buildDialUri(raw) {
  return getDialProtocol() + sanitizeNumber(raw);
}

// Trigger the AGFEO Dashboard dial and return metadata about the HANDOFF.
//
// Returned shape (see file header for the one-way caveat):
//   { raw, number, protocol, uri, dialedAt, launched }
// `launched` only means the protocol URI was handed to the OS — never that the
// call connected. `dialedAt` is included so a later logging step can pair the
// dial with a manually captured outcome + duration.
export function dial(raw) {
  const number = sanitizeNumber(raw);
  const protocol = getDialProtocol();
  const uri = protocol + number;
  const result = {
    raw,
    number,
    protocol,
    uri,
    dialedAt: new Date().toISOString(),
    launched: false,
  };

  if (!isDialable(raw)) {
    return result;
  }

  try {
    if (protocol === MOBILE_DIAL_PROTOCOL) {
      // `tel:` ist ein regulaerer, vom Betriebssystem unterstuetzter Link — die
      // Telefon-App uebernimmt die Seite ohnehin nicht dauerhaft. Die
      // iframe-Huelle darunter ist fuer `tksuite:` gedacht (unregistrierter
      // Custom-Handler soll die Seite nicht leerraeumen) und wird von iOS Safari
      // bei `tel:` teils blockiert.
      window.location.href = uri;
    } else {
      // A hidden iframe navigation triggers the registered protocol handler
      // without blanking/replacing the current page the way a top-level
      // `window.location = uri` can when no handler is installed.
      const frame = document.createElement("iframe");
      frame.style.display = "none";
      frame.src = uri;
      document.body.appendChild(frame);
      window.setTimeout(() => {
        if (frame.parentNode) {
          frame.parentNode.removeChild(frame);
        }
      }, 1000);
    }
    result.launched = true;
  } catch (e) {
    // Some environments block iframe protocol navigation — fall back to a
    // top-level assignment, which still invokes the registered handler.
    try {
      window.location.href = uri;
      result.launched = true;
    } catch (err) {
      result.launched = false;
    }
  }

  return result;
}

// Format a duration in seconds as "m:ss" (e.g. 75 -> "1:15"). Useful for the
// upcoming logging UI.
export function formatDuration(totalSeconds) {
  const seconds = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${minutes}:${String(rest).padStart(2, "0")}`;
}

// Gegenstueck zu formatDuration: "1:15" -> 75, "90" -> 90. Nur Ziffern und ein
// Doppelpunkt zaehlen, alles andere faellt weg — getippt wird in einem Feld, das
// sonst nur eine Uhr anzeigt.
export function parseDuration(text) {
  const parts = String(text ?? "")
    .replace(/[^\d:]/g, "")
    .split(":");
  if (parts.length > 2 || parts.every((p) => p === "")) return null;
  const [a, b] = parts;
  const seconds = parts.length === 2 ? Number(a || 0) * 60 + Number(b || 0) : Number(a);
  return Number.isFinite(seconds) ? Math.max(0, seconds) : null;
}

// Gespraechsdauer in Worten: 71 -> "1 min 11 sec". Die Karte im Verlauf liest
// sich so schneller als "1:11", weil die Einheit dabeisteht. t wird
// hereingegeben, damit die Funktion ohne i18n-Aufbau pruefbar bleibt.
export function humanDuration(totalSeconds, t) {
  const total = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  if (!total) return "";
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  if (!minutes) return t("{{count}} sec", { count: seconds });
  const m = t("{{count}} min", { count: minutes });
  return seconds ? `${m} ${t("{{count}} sec", { count: seconds })}` : m;
}

// Stopwatch hook for measuring call duration. Decoupled from dialing and
// logging so any caller can reuse it: `{ elapsedSeconds, running, start, stop,
// reset }`.
export function useCallTimer() {
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [running, setRunning] = useState(false);
  const intervalRef = useRef(null);
  const baselineRef = useRef(null);
  // Der Stand auch als Ref, damit start() direkt nach set() vom neuen Wert
  // ankert und nicht vom Stand des letzten Renders.
  const elapsedRef = useRef(0);

  const apply = useCallback((seconds) => {
    elapsedRef.current = seconds;
    setElapsedSeconds(seconds);
  }, []);

  const clear = useCallback(() => {
    if (intervalRef.current) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const start = useCallback(() => {
    if (intervalRef.current) return;
    // Anchor to wall-clock time so the count stays accurate even if the tab is
    // throttled in the background.
    baselineRef.current = Date.now() - elapsedRef.current * 1000;
    setRunning(true);
    intervalRef.current = window.setInterval(() => {
      apply(Math.floor((Date.now() - baselineRef.current) / 1000));
    }, 1000);
  }, [apply]);

  const stop = useCallback(() => {
    clear();
    setRunning(false);
  }, [clear]);

  // Von Hand korrigierte Zeit: der Zaehler laeuft danach von dort weiter, statt
  // die Korrektur beim naechsten Tick zu ueberschreiben.
  const set = useCallback(
    (seconds) => {
      const value = Math.max(0, Math.floor(Number(seconds) || 0));
      apply(value);
      baselineRef.current = Date.now() - value * 1000;
    },
    [apply]
  );

  const reset = useCallback(() => {
    clear();
    setRunning(false);
    apply(0);
    baselineRef.current = null;
  }, [clear, apply]);

  // Clean up the interval on unmount.
  useEffect(() => clear, [clear]);

  return { elapsedSeconds, running, start, stop, reset, set };
}
